/**
 * Slide renderers (SCN-612) — one full-bleed 16:9 layout per slide type.
 *  - MenuItem: item image background + gradient + name / price badge / description
 *  - Image: object-fit:cover + optional bottom caption
 *  - Video (uploaded or YouTube): see the timing rule below
 *  - CustomHtml: sandboxed iframe filling the viewport
 *
 * Video timing rule (uploaded and YouTube alike) — `D` is the slide's target duration
 * (durationSeconds, or the playlist default) and `V` is the video's own natural length:
 *  - V < D: honour the `loop` flag. Looping fills the remaining time and the slide advances on a
 *    timer at D. Not looping plays the video once and then holds (frozen) until D.
 *  - V >= D: always play the video through once, ignoring `loop` — it is never cut short, and
 *    advances on the video's natural end (which is already at or past D).
 *  - V unknown (metadata/API never arrives): fall back to a timer at D plus a short grace period,
 *    so a broken video or blocked API can't hang the rotator.
 */
import { useEffect, useRef, useState } from 'react';
import type {
  CustomHtmlPayload,
  DeviceSlide,
  ImagePayload,
  MenuItemPayload,
  VideoPayload,
} from '@/types';
import { loadYouTubeIframeApi, YouTubePlayer } from '@/youtube';
import { canTrySound, markUnmutedAutoplayBlocked, onSoundAllowed } from '@/autoplay';

const isYouTube = (url: string) => /(?:youtube\.com|youtu\.be)/i.test(url);

function youtubeId(url: string): string | null {
  const patterns = [
    /youtube\.com\/watch\?(?:.*&)?v=([\w-]{11})/i,
    /youtu\.be\/([\w-]{11})/i,
    /youtube\.com\/shorts\/([\w-]{11})/i,
    /youtube\.com\/embed\/([\w-]{11})/i,
    /youtube\.com\/live\/([\w-]{11})/i,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return m[1];
  }
  return null;
}

/** Fallback plain embed (no timing control) for when the IFrame Player API can't be loaded. */
function fallbackEmbedUrl(id: string, loop: boolean): string {
  const params = new URLSearchParams({
    autoplay: '1',
    mute: '1',
    controls: '0',
    modestbranding: '1',
    rel: '0',
    playsinline: '1',
  });
  if (loop) {
    params.set('loop', '1');
    params.set('playlist', id);
  }
  return `https://www.youtube.com/embed/${id}?${params.toString()}`;
}

interface SlideProps {
  slide: DeviceSlide;
  /** The slide's own duration, or the playlist default — "D" in the timing rule above. */
  targetDurationSeconds: number;
  /** Fires once this slide has been shown for its full effective duration. */
  onAdvance: () => void;
}

export function Slide({ slide, targetDurationSeconds, onAdvance }: SlideProps) {
  switch (slide.type) {
    case 'MenuItem':
      return <MenuItemSlide payload={slide.payload as MenuItemPayload} />;
    case 'Image':
      return <ImageSlide payload={slide.payload as ImagePayload} />;
    case 'Video': {
      const payload = slide.payload as VideoPayload;
      return isYouTube(payload.videoUrl) ? (
        <YouTubeVideoSlide
          payload={payload}
          targetDurationSeconds={targetDurationSeconds}
          onAdvance={onAdvance}
        />
      ) : (
        <UploadedVideoSlide
          payload={payload}
          targetDurationSeconds={targetDurationSeconds}
          onAdvance={onAdvance}
        />
      );
    }
    case 'CustomHtml':
      return <CustomHtmlSlide payload={slide.payload as CustomHtmlPayload} />;
    default:
      return null;
  }
}

function MenuItemSlide({ payload }: { payload: MenuItemPayload }) {
  return (
    <div className="slide slide--menu">
      {payload.imageUrl ? (
        <img className="slide__bg" src={payload.imageUrl} alt="" />
      ) : (
        <div className="slide__bg slide__bg--fallback" />
      )}
      <div className="slide__scrim" />
      <div className="slide__caption-block">
        <div className="slide__tag">Featured</div>
        <div className="slide__title">{payload.name || 'Menu item'}</div>
        {payload.showDescription && payload.description && (
          <div className="slide__desc">{payload.description}</div>
        )}
        {payload.showPrice && payload.price != null && (
          <span className="slide__price">{formatPrice(payload.price)}</span>
        )}
      </div>
    </div>
  );
}

function ImageSlide({ payload }: { payload: ImagePayload }) {
  return (
    <div className="slide slide--image">
      <img className="slide__bg" src={payload.imageUrl} alt="" />
      {payload.caption && (
        <>
          <div className="slide__scrim slide__scrim--thin" />
          <div className="slide__caption-strip">{payload.caption}</div>
        </>
      )}
    </div>
  );
}

interface VideoSlideProps {
  payload: VideoPayload;
  targetDurationSeconds: number;
  onAdvance: () => void;
}

function UploadedVideoSlide({ payload, targetDurationSeconds, onAdvance }: VideoSlideProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [loopElement, setLoopElement] = useState(payload.loop);
  // `muted:false` slides try to play with sound. The browser blocks that without a user gesture,
  // so we let the native `autoPlay` take the earliest shot, then check shortly after: if it's
  // still paused we mute + resume (and remember it for the session so later slides don't flicker
  // through the same failed attempt). A user tapping the screen lifts the block — see autoplay.ts.
  const wantsSound = !payload.muted;
  const [mutedOverride, setMutedOverride] = useState(() => wantsSound && !canTrySound());

  const onAdvanceRef = useRef(onAdvance);
  onAdvanceRef.current = onAdvance;
  const firedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  // Only a natural `ended` while in this mode should advance the slide (see the timing rule).
  const playThroughRef = useRef(false);

  const fire = () => {
    if (firedRef.current) return;
    firedRef.current = true;
    clearTimeout(timerRef.current);
    onAdvanceRef.current();
  };

  useEffect(() => {
    firedRef.current = false;
    playThroughRef.current = false;
    setLoopElement(payload.loop);
    setMutedOverride(wantsSound && !canTrySound());
    // Grace fallback in case loadedmetadata never fires (bad URL / network) — never hang the wall.
    timerRef.current = setTimeout(fire, (targetDurationSeconds + 5) * 1000);
    return () => clearTimeout(timerRef.current);
    // `fire`/`payload.loop` are read via refs/closures that stay current — the effect only needs
    // to re-run when the video or its target duration actually changes.
  }, [payload.videoUrl, targetDurationSeconds]);

  // The <video autoPlay> attribute takes the earliest possible shot at playback (it fires before
  // any effect runs, so it still catches the page's transient user activation on the first slide).
  // The attribute gives no signal when the browser refuses an unmuted play, so we poll once shortly
  // after: if it's still paused, fall back to muted playback and remember it for the session so
  // later slides don't flicker through the same failed attempt.
  useEffect(() => {
    if (!wantsSound || !canTrySound()) return;
    const check = setTimeout(() => {
      const video = videoRef.current;
      if (!video || !video.paused) return;
      markUnmutedAutoplayBlocked();
      video.muted = true;
      setMutedOverride(true);
      video.play().catch(() => {
        /* even muted playback failed — the safety timers still advance the rotator */
      });
    }, 700);
    return () => clearTimeout(check);
  }, [payload.videoUrl, wantsSound]);

  // A real user interaction lifts the autoplay-with-sound block — unmute the slide that's playing
  // now (future slides read the refreshed policy on mount).
  useEffect(() => {
    if (!wantsSound) return;
    return onSoundAllowed(() => {
      const video = videoRef.current;
      if (!video) return;
      video.muted = false;
      setMutedOverride(false);
      video.play().catch(() => {});
    });
  }, [payload.videoUrl, wantsSound]);

  const handleLoadedMetadata = () => {
    const duration = videoRef.current?.duration;
    if (!duration || !Number.isFinite(duration)) return;

    clearTimeout(timerRef.current);
    if (duration < targetDurationSeconds) {
      // Shorter than the slide — honour `loop`; either way we advance once D elapses.
      playThroughRef.current = false;
      setLoopElement(payload.loop);
      timerRef.current = setTimeout(fire, targetDurationSeconds * 1000);
    } else {
      // Already long enough — never cut it short, play it through once regardless of `loop`.
      // We still advance on the natural `ended` event, but keep a safety-net timer (duration +
      // grace) in case playback stalls or never actually starts — the earlier bug this fixes.
      playThroughRef.current = true;
      setLoopElement(false);
      timerRef.current = setTimeout(fire, (duration + 5) * 1000);
    }
  };

  const handleEnded = () => {
    if (playThroughRef.current) fire();
    // Otherwise: shorter than D and not looping — hold on the last frame until the D-timer fires.
  };

  return (
    <div className="slide slide--video">
      <video
        ref={videoRef}
        className="slide__bg"
        src={payload.videoUrl}
        autoPlay
        muted={payload.muted || mutedOverride}
        loop={loopElement}
        playsInline
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
      />
      {payload.caption && <div className="slide__caption-strip">{payload.caption}</div>}
    </div>
  );
}

function YouTubeVideoSlide({ payload, targetDurationSeconds, onAdvance }: VideoSlideProps) {
  const id = youtubeId(payload.videoUrl);
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YouTubePlayer | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const muteCheckRef = useRef<ReturnType<typeof setTimeout>>();
  const firedRef = useRef(false);
  const onAdvanceRef = useRef(onAdvance);
  onAdvanceRef.current = onAdvance;
  const [apiFailed, setApiFailed] = useState(false);

  // Whether we're allowed to even try playing with sound this session.
  const wantsSound = !payload.muted && canTrySound();

  const fire = () => {
    if (firedRef.current) return;
    firedRef.current = true;
    clearTimeout(timerRef.current);
    onAdvanceRef.current();
  };

  useEffect(() => {
    firedRef.current = false;
    setApiFailed(false);

    if (!id) {
      // Can't even parse a video id — nothing to play.
      timerRef.current = setTimeout(fire, targetDurationSeconds * 1000);
      return () => clearTimeout(timerRef.current);
    }

    let cancelled = false;
    // Grace fallback in case the API / player never calls back.
    timerRef.current = setTimeout(fire, (targetDurationSeconds + 8) * 1000);

    loadYouTubeIframeApi()
      .then((YT) => {
        if (cancelled || !containerRef.current) return;

        playerRef.current = new YT.Player(containerRef.current, {
          videoId: id,
          playerVars: {
            autoplay: 1,
            mute: wantsSound ? 0 : 1,
            controls: 0,
            modestbranding: 1,
            rel: 0,
            playsinline: 1,
          },
          events: {
            onReady: (e) => {
              // If we asked for sound, the browser may still refuse to autoplay — if the player
              // isn't actually PLAYING shortly after, mute and restart (and remember for the
              // session) so it doesn't just sit paused.
              if (wantsSound) {
                muteCheckRef.current = setTimeout(() => {
                  if (cancelled) return;
                  if (e.target.getPlayerState() !== YT.PlayerState.PLAYING) {
                    markUnmutedAutoplayBlocked();
                    e.target.mute();
                    e.target.playVideo();
                  }
                }, 1600);
              }

              const duration = e.target.getDuration();
              if (!duration || !Number.isFinite(duration) || duration <= 0) return; // unknown — grace timer stands
              clearTimeout(timerRef.current);
              if (duration < targetDurationSeconds) {
                timerRef.current = setTimeout(fire, targetDurationSeconds * 1000);
              }
              // duration >= target: play through once, advance on ENDED below — no timer.
            },
            onStateChange: (e) => {
              if (e.data !== YT.PlayerState.ENDED) return;
              const duration = e.target.getDuration();
              if (duration && duration < targetDurationSeconds && payload.loop) {
                e.target.seekTo(0, true);
                e.target.playVideo();
                return; // loop-fill — the target-duration timer set in onReady advances the slide
              }
              if (!duration || duration >= targetDurationSeconds) fire(); // play-through
              // else: shorter than D and not looping — hold; the timer fires later
            },
            onError: () => fire(),
          },
        });
      })
      .catch(() => {
        if (!cancelled) setApiFailed(true);
      });

    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
      clearTimeout(muteCheckRef.current);
      playerRef.current?.destroy();
      playerRef.current = null;
    };
    // `fire` is read via a ref that stays current — only re-create the player when the video,
    // its target duration, or the loop/mute intent actually change.
  }, [id, targetDurationSeconds, payload.loop, wantsSound]);

  // A real user interaction lifts the autoplay-with-sound block — unmute the player that's up now
  // without tearing it down (future slides mount with the refreshed policy).
  useEffect(() => {
    if (payload.muted) return;
    return onSoundAllowed(() => {
      playerRef.current?.unMute();
      playerRef.current?.playVideo();
    });
  }, [id, payload.muted]);

  return (
    <div className="slide slide--video">
      {apiFailed && id ? (
        <iframe
          className="slide__bg"
          src={fallbackEmbedUrl(id, payload.loop)}
          title="Video slide"
          allow="autoplay; encrypted-media; picture-in-picture"
          frameBorder={0}
        />
      ) : id ? (
        <div className="slide__bg">
          <div ref={containerRef} />
        </div>
      ) : (
        <div className="slide__bg slide__bg--fallback" />
      )}
      {payload.caption && <div className="slide__caption-strip">{payload.caption}</div>}
    </div>
  );
}

function CustomHtmlSlide({ payload }: { payload: CustomHtmlPayload }) {
  return (
    <div className="slide slide--html">
      <iframe
        className="slide__bg"
        title="HTML slide"
        sandbox="allow-scripts"
        srcDoc={payload.html}
      />
      {payload.caption && <div className="slide__caption-strip">{payload.caption}</div>}
    </div>
  );
}

function formatPrice(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}
